/**
 * Centralized logging infrastructure
 * Errors and warnings are forwarded to Sentry when configured; info uses structured console output only.
 */

import * as Sentry from "@sentry/nextjs";
import type { ZodError } from "zod";

/**
 * Compact Zod issue summary for log contexts — first 5 issues only so
 * structured logs stay readable in Sentry/dashboards. Each entry is prefixed
 * with the dotted path so generic messages (e.g. Zod 4's "Invalid input" for
 * discriminated-union mismatches) stay actionable. The `note` that Zod 4
 * attaches to union errors (e.g. "No matching discriminator") is appended
 * when present so on-call sees the cause without opening the payload.
 */
export function summarizeZodIssues(error: ZodError, max = 5): string[] {
  return error.issues.slice(0, max).map((issue) => {
    const path = issue.path.length > 0 ? issue.path.join(".") : "";
    // Zod 4 attaches an optional `note` to union/discriminator errors but
    // does not surface it on the base `ZodIssue` type. Read it via `in`
    // narrowing rather than a `as unknown as` cast so the compiler still
    // catches typos.
    const note =
      "note" in issue && typeof issue.note === "string"
        ? ` (${issue.note})`
        : "";
    return path ? `${path}: ${issue.message}${note}` : `${issue.message}${note}`;
  });
}

export type LogSeverity = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW" | "WARN" | "INFO";

interface LogContext {
  errorId?: string;
  severity?: LogSeverity;
  [key: string]: unknown;
}

const _warned = new Set<string>();
function warnOnce(key: string, message: string, error?: unknown): void {
  if (_warned.has(key)) return;
  _warned.add(key);
  console.error(message, error);
}

/**
 * Log error with Sentry integration
 * Outside development, sends to Sentry and logs structured JSON
 * In development, logs to console with formatted output
 */
/**
 * Messages along the error's `cause` chain (capped at 3) so logs carry the
 * underlying failure — e.g. the endpoint's own error body behind an
 * app-level "the endpoint rejected your key" wrapper.
 */
function causeMessages(error: Error): string[] {
  const messages: string[] = [];
  let current: unknown = error.cause;
  while (current !== undefined && current !== null && messages.length < 3) {
    if (current instanceof Error) {
      messages.push(current.message);
      current = current.cause;
    } else {
      messages.push(String(current));
      break;
    }
  }
  return messages;
}

export function logError(
  error: unknown,
  message: string,
  context?: LogContext
): void {
  const resolvedError = error instanceof Error ? error : new Error(String(error));
  const errorMessage = resolvedError.message;
  const errorStack = resolvedError.stack;
  const causes = causeMessages(resolvedError);

  const logData = {
    message,
    errorMessage,
    ...(causes.length > 0 ? { causes } : {}),
    ...context,
    timestamp: new Date().toISOString(),
  };

  // Development: Rich console output
  if (process.env.NODE_ENV === 'development') {
    console.error(`[ERROR] ${message}`, {
      ...logData,
      stack: errorStack,
    });
    return;
  }

  try {
    Sentry.captureException(resolvedError, {
      tags: {
        errorId: context?.errorId,
        severity: context?.severity,
      },
      extra: logData,
    });
  } catch (sentryError) {
    warnOnce("logError", "[logError] Sentry.captureException failed:", sentryError);
  }

  // Structured JSON logging (browser console)
  console.error(JSON.stringify({
    level: 'error',
    ...logData,
    error: {
      message: errorMessage,
      stack: errorStack,
    },
  }));
}

/**
 * Log warning with context and Sentry integration
 * Outside development, sends to Sentry as a warning-level message and logs structured JSON
 * In development, logs to console with formatted output
 */
export function logWarning(
  message: string,
  context?: LogContext
): void {
  const logData = {
    message,
    ...context,
    timestamp: new Date().toISOString(),
  };

  if (process.env.NODE_ENV === 'development') {
    console.warn(`[WARNING] ${message}`, logData);
    return;
  }

  try {
    Sentry.captureMessage(message, {
      level: "warning",
      tags: {
        errorId: context?.errorId,
        severity: context?.severity,
      },
      extra: logData,
    });
  } catch (sentryError) {
    warnOnce("logWarning", "[logWarning] Sentry.captureMessage failed:", sentryError);
  }

  console.warn(JSON.stringify({
    level: 'warning',
    ...logData,
  }));
}

/**
 * Log info message with context
 */
export function logInfo(
  message: string,
  context?: Record<string, unknown>
): void {
  const logData = {
    message,
    ...context,
    timestamp: new Date().toISOString(),
  };

  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console -- Development logging output
    console.log(`[INFO] ${message}`, logData);
  } else {
    // eslint-disable-next-line no-console -- Structured production logging
    console.log(JSON.stringify({
      level: 'info',
      ...logData,
    }));
  }
}
